import React, { useState, useRef, useCallback } from "react";
import { Button } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import grapesjs, { Editor as GjsEditor } from "grapesjs";
import GjsEditorReact from "@grapesjs/react";
import "grapesjs/dist/css/grapes.min.css";
// @ts-ignore — no types available
import grapesjsPresetNewsletter from "grapesjs-preset-newsletter";
import "./template-builder.css";

/**
 * Shared GrapesJS visual template builder, used by BOTH the email-templates and
 * document-templates (invoice/receipt) editors. Everything channel-specific is
 * injected via props:
 *   - which fields to show (subject is email-only)
 *   - where images upload to
 *   - an optional variable palette (Mustache merge-fields for documents)
 *   - load/save adapters that map to the right DB column (Message vs Mustache)
 *
 * The premium bits — GrapesJS drag-and-drop + newsletter blocks + device
 * preview + server-side S3 image upload + round-trippable DesignJson — live here
 * once, so both channels get the same builder.
 */

export interface TemplateVariable {
  /** display label, e.g. "Invoice number" */
  label: string;
  /** the Mustache tag inserted into the canvas, e.g. "{{invoiceNumber}}" */
  tag: string;
  /** optional richer HTML content (e.g. an <img> for a logo, a section wrapper) */
  content?: string;
}

export interface TemplateVariableGroup {
  label: string;
  items: TemplateVariable[];
}

export interface LoadedTemplate {
  name: string;
  subject: string;
  html: string | null;
  designJson: string | null;
}

export interface TemplateBuilderProps {
  templateId: number | null;
  backPath: string;
  namePlaceholder?: string;
  /** email shows a Subject line; documents don't */
  showSubject?: boolean;
  subjectPlaceholder?: string;
  /** GrapesJS assetManager upload endpoint (already channel-namespaced) */
  uploadUrl: string;
  /** optional Mustache merge-field palette (documents) */
  variables?: TemplateVariableGroup[];
  loadTemplate: (id: number) => Promise<LoadedTemplate>;
  saveTemplate: (payload: {
    id: number | null;
    name: string;
    subject: string | null;
    html: string;
    designJson: string;
  }) => Promise<{ id: number }>;
  /** path to replace in the URL bar after the first save mints an id */
  editUrl: (id: number) => string;
  /** optional extra toolbar controls (e.g. the email Send-Test button) */
  extraToolbar?: (ctx: { id: number | null; name: string }) => React.ReactNode;
}

const getAuthHeader = (): string | null => {
  try {
    const stored = localStorage.getItem("currentUser");
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed?.token ? `Bearer ${parsed.token}` : null;
  } catch {
    return null;
  }
};

const TemplateBuilder: React.FC<TemplateBuilderProps> = ({
  templateId,
  backPath,
  namePlaceholder = "Template name",
  showSubject = false,
  subjectPlaceholder = "Subject line",
  uploadUrl,
  variables,
  loadTemplate,
  saveTemplate,
  editUrl,
  extraToolbar,
}) => {
  const history = useHistory();

  const [name, setName] = useState("Untitled Template");
  const [subject, setSubject] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actualId, setActualId] = useState<number | null>(templateId);
  const editorRef = useRef<GjsEditor | null>(null);

  const assetManagerConfig = React.useMemo(() => {
    const auth = getAuthHeader();
    const config: Record<string, any> = {
      upload: uploadUrl,
      uploadName: "files[]",
      multiUpload: true,
      autoAdd: true,
    };
    if (auth) config["headers"] = { Authorization: auth };
    return config;
  }, [uploadUrl]);

  // Register the Mustache merge-field palette as GrapesJS blocks under an
  // "Invoice Fields" category — drag a field onto the canvas to insert its tag.
  const registerVariableBlocks = useCallback(
    (editor: GjsEditor) => {
      if (!variables || variables.length === 0) return;
      const bm = editor.BlockManager;
      variables.forEach((group) => {
        group.items.forEach((item) => {
          const blockId = `var-${item.tag.replace(/[^a-zA-Z0-9]/g, "")}`;
          if (bm.get(blockId)) return;
          bm.add(blockId, {
            label: item.label,
            category: `Fields · ${group.label}`,
            content: item.content ?? `<span data-gjs-type="text">${item.tag}</span>`,
            attributes: { class: "tb-var-block" },
          });
        });
      });
    },
    [variables],
  );

  const handleEditorReady = useCallback(
    async (editor: GjsEditor) => {
      editorRef.current = editor;
      registerVariableBlocks(editor);

      if (templateId) {
        try {
          const data = await loadTemplate(templateId);
          setName(data.name || "Untitled");
          setSubject(data.subject || "");
          if (data.designJson) {
            try {
              editor.loadProjectData(JSON.parse(data.designJson));
            } catch {
              if (data.html) editor.setComponents(data.html);
            }
          } else if (data.html) {
            editor.setComponents(data.html);
          }
        } catch (err) {
          console.error("Failed to load template into editor:", err);
          setError("Could not load this template. It may have been deleted.");
        }
      }
    },
    [templateId, loadTemplate, registerVariableBlocks],
  );

  const handleSave = async () => {
    if (!editorRef.current) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const editor = editorRef.current;
      const html = editor.getHtml();
      const css = editor.getCss();
      const fullHtml = css ? `<style>${css}</style>${html}` : html;
      const designJson = JSON.stringify(editor.getProjectData());

      const res = await saveTemplate({
        id: actualId,
        name: name.trim() || "Untitled Template",
        subject: showSubject ? subject.trim() || null : null,
        html: fullHtml,
        designJson,
      });

      if (!actualId && res.id) {
        setActualId(res.id);
        window.history.replaceState(null, "", editUrl(res.id));
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err: any) {
      console.error("Failed to save template:", err);
      setError(err?.response?.data?.error?.message || err?.message || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="template-builder">
      <div className="tb-toolbar">
        <Button variant="secondary" size="sm" onClick={() => history.push(backPath)}>
          &larr; Back
        </Button>
        <input
          type="text"
          className="tb-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={namePlaceholder}
        />
        {showSubject && (
          <input
            type="text"
            className="tb-subject-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={subjectPlaceholder}
          />
        )}
        <div style={{ flex: 1 }} />
        {error && <span className="tb-error">{error}</span>}
        {extraToolbar?.({ id: actualId, name })}
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : saved ? "Saved!" : "Save"}
        </Button>
      </div>

      <div className="tb-editor-wrap">
        <GjsEditorReact
          grapesjs={grapesjs}
          options={{
            height: "100%",
            storageManager: false,
            undoManager: { trackSelection: false },
            plugins: [grapesjsPresetNewsletter],
            canvas: {
              styles: [
                "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
              ],
            },
            assetManager: assetManagerConfig,
          }}
          onReady={handleEditorReady}
        />
      </div>
    </div>
  );
};

export default TemplateBuilder;
